import { Head, Link, usePage } from '@inertiajs/react';
import { useEffect, useRef } from 'react';
import MainLayout, { useModals } from '@/Layouts/MainLayout';
import { useHomeContent } from '@/lib/homeContent';
import { syncAutoplayVideo, useResponsiveMedia } from '@/lib/responsiveMedia';
import Hero from '@/Components/Frontend/Hero';
import HomeV1 from '@/Components/HomeVariants/HomeV1';
import HomeV2 from '@/Components/HomeVariants/HomeV2';
import HomeV3 from '@/Components/HomeVariants/HomeV3';
import HomeV4 from '@/Components/HomeVariants/HomeV4';
import HomeV5 from '@/Components/HomeVariants/HomeV5';
import HomePopup from '@/Components/HomePopup';
import { HomeQuickActions, HomeImageTrends, HomeVideoTrends, VideoCard } from '@/Components/HomeVariants/HomeSections';

function IndexContent({ page }) {
    const { openRegister } = useModals() || {};
    const { settings } = usePage().props;
    const c = useHomeContent(page?.content);
    const { canAutoplayDecorativeVideo } = useResponsiveMedia();
    const introVideoRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
            document.querySelectorAll('[data-animate]').forEach((el) => {
                el.classList.remove('opacity-0', 'translate-y-8');
            });
            return undefined;
        }

        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fade-in');
                    entry.target.classList.remove('opacity-0', 'translate-y-8');
                }
            });
        }, observerOptions);

        // Observe all sections with data-animate attribute  
        document.querySelectorAll('[data-animate]').forEach(el => {
            el.classList.add('opacity-0', 'translate-y-8', 'transition-all', 'duration-700');
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!canAutoplayDecorativeVideo || !c.intro_video || !introVideoRef.current) {
            return undefined;
        }

        return syncAutoplayVideo(introVideoRef.current);
    }, [canAutoplayDecorativeVideo, c.intro_video]);

    return (
        <>
            <Head>
                <title>{settings?.title || settings?.name || 'Home'}</title>
                <meta name="description" content={settings?.description || 'The Creative Engine'} />
                {settings?.favicon && <link rel="icon" type="image/x-icon" href={settings.favicon} />}
            </Head>
            {/* Hero Section */}
            {/* Hero Section */}
            <Hero openRegister={openRegister} content={c} />

            {/* Stats Section */}
            <section data-animate className="py-16 bg-gradient-to-b from-slate-100 to-white dark:from-[#1a1612] dark:to-background-dark transition-colors duration-300">
                <div className="max-w-[1200px] mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="text-center">
                                <div className="text-4xl md:text-5xl font-black text-primary mb-2">{c[`stat_number_${i}`]}</div>
                                <div className="text-sm text-slate-500 dark:text-white/40 uppercase tracking-wider font-medium transition-colors duration-300">{c[`stat_label_${i}`]}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Intro Section */}
            {c.show_intro !== false && (
                <section data-animate className="py-24 bg-white dark:bg-background-dark transition-colors duration-300">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="space-y-8 text-center lg:text-left order-2 lg:order-1">
                            <div className="inline-block px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider mb-4">
                                {c.intro_badge}
                            </div>
                            <h2 className="text-5xl md:text-7xl font-black leading-[1.05] tracking-tight text-slate-900 dark:text-white transition-colors duration-300"
                                dangerouslySetInnerHTML={{ __html: c.intro_title }} />
                            <p className="text-slate-600 dark:text-white/60 text-xl leading-relaxed max-w-xl mx-auto lg:mx-0 transition-colors duration-300"
                                dangerouslySetInnerHTML={{ __html: c.intro_desc }} />
                            <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-6">
                                {c.intro_check_1 && <div className="flex items-center gap-3 text-slate-700 dark:text-white/80 transition-colors duration-300">
                                    <span className="material-symbols-outlined text-primary text-2xl">check_circle</span>
                                    <span className="font-semibold">{c.intro_check_1}</span>
                                </div>}
                                {c.intro_check_2 && <div className="flex items-center gap-3 text-slate-700 dark:text-white/80 transition-colors duration-300">
                                    <span className="material-symbols-outlined text-primary text-2xl">check_circle</span>
                                    <span className="font-semibold">{c.intro_check_2}</span>
                                </div>}
                            </div>
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-3xl"></div>
                            <div className="relative bg-gradient-to-br from-white to-slate-100 dark:from-[#28211b] dark:to-[#1a1410] rounded-3xl overflow-hidden aspect-video border-2 border-slate-200 dark:border-white/10 shadow-2xl transition-colors duration-300">
                                {canAutoplayDecorativeVideo && c.intro_video ? (
                                    <video ref={introVideoRef} className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline preload="auto">
                                        <source src={c.intro_video} type="video/mp4" />
                                    </video>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 via-slate-100 to-slate-200 dark:from-primary/10 dark:via-[#221b16] dark:to-[#120d09]">
                                        <div className="flex size-20 items-center justify-center rounded-full bg-white/70 text-primary shadow-lg dark:bg-black/30">
                                            <span className="material-symbols-outlined text-4xl">movie</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Quick Actions Section */}
            {c.show_quick_actions !== false && (
                <section id="quick-actions" data-animate className="py-24 bg-white dark:bg-background-dark transition-colors duration-300">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-lg">bolt</span>
                                {c.quick_actions_badge || 'AI Models'}
                            </div>
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300" dangerouslySetInnerHTML={{ __html: c.quick_actions_title || 'Explore AI Capability' }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed transition-colors duration-300">{c.quick_actions_desc || 'Access all premium generation models and tools instantly from your dashboard.'}</p>
                        </div>
                        <HomeQuickActions />
                    </div>
                </section>
            )}

            {/* Image Trends Section */}
            {c.show_trends !== false && (
                <section id="image-trends" data-animate className="py-24 bg-gradient-to-b from-white to-slate-50 dark:from-background-dark dark:to-[#1a1612] transition-colors duration-300">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-lg">auto_awesome</span>
                                {c.trends_badge || 'Trending'}
                            </div>
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300" dangerouslySetInnerHTML={{ __html: c.trends_title || 'Discover <span class="text-primary">AI Image Trends</span>' }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed transition-colors duration-300">{c.trends_desc || 'Explore our most popular AI templates and creative presets used by top creators.'}</p>
                        </div>
                        <HomeImageTrends />
                    </div>
                </section>
            )}

            {/* Image Creation Section */}
            {c.show_images !== false && (
                <section id="images" data-animate className="py-24 bg-gradient-to-b from-white via-slate-50 to-slate-100 dark:from-background-dark dark:via-[#1a1612] dark:to-[#1a1612] transition-colors duration-300">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-lg">image</span>
                                {c.image_studio_badge}
                            </div>
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300"
                                dangerouslySetInnerHTML={{ __html: c.image_studio_title }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed transition-colors duration-300">{c.image_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.image_cards.map((item, i) => (
                                /* ── Flip Card Container ── */
                                <div
                                    key={i}
                                    className="aspect-[3/4] cursor-pointer"
                                    style={{ perspective: '1200px' }}
                                    onMouseEnter={e => e.currentTarget.firstChild.style.transform = 'rotateY(180deg)'}
                                    onMouseLeave={e => e.currentTarget.firstChild.style.transform = 'rotateY(0deg)'}
                                >
                                    <div
                                        className="relative w-full h-full transition-transform duration-700 ease-in-out"
                                        style={{ transformStyle: 'preserve-3d' }}
                                    >
                                        {/* ── FRONT: image + title ── */}
                                        <div
                                            className="absolute inset-0 rounded-2xl overflow-hidden border-2 border-slate-200 dark:border-white/5"
                                            style={{ backfaceVisibility: 'hidden' }}
                                        >
                                            {/* Main image fills card */}
                                            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${item.main}')` }} />
                                            {/* Title bar */}
                                            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 bg-primary/70 px-4 py-4">
                                                <h3 className="text-white font-black text-sm tracking-widest uppercase leading-tight">{item.title}</h3>
                                                <span className="material-symbols-outlined text-white text-[18px] flex-shrink-0">open_in_new</span>
                                            </div>
                                        </div>

                                        {/* ── BACK: primary overlay + 2 sub-images + "+" ── */}
                                        <div
                                            className="absolute inset-0 rounded-2xl overflow-hidden border-2 border-primary/60"
                                            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                        >
                                            {/* Background = main image */}
                                            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${item.main}')` }} />
                                            {/* Primary 80% overlay */}
                                            <div className="absolute inset-0 bg-primary/80" />
                                            {/* Content */}
                                            <div className="relative z-10 h-full flex flex-col items-center justify-center gap-6 p-5">
                                                <div className="flex items-center gap-5">
                                                    <div
                                                        className="size-24 sm:size-28 rounded-xl bg-cover bg-center border-2 border-white/40 shadow-2xl"
                                                        style={{ backgroundImage: `url('${item.sub1}')` }}
                                                    />
                                                    <span className="text-white font-black text-4xl leading-none drop-shadow-lg">+</span>
                                                    <div
                                                        className="size-24 sm:size-28 rounded-xl bg-cover bg-center border-2 border-white/40 shadow-2xl"
                                                        style={{ backgroundImage: `url('${item.sub2}')` }}
                                                    />
                                                </div>
                                                <p className="text-white text-sm font-black uppercase tracking-widest text-center">
                                                    {item.title}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Video Trends Section */}
            {c.show_video_trends !== false && (
                <section id="video-trends" data-animate className="py-24 bg-gradient-to-b from-slate-100 via-white to-slate-50 dark:from-[#1a1612] dark:to-background-dark transition-colors duration-300">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-lg">smart_display</span>
                                {c.video_trends_badge || 'Video Trends'}
                            </div>
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300" dangerouslySetInnerHTML={{ __html: c.video_trends_title || 'Trending <span class="text-primary">Avatar Templates</span>' }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed transition-colors duration-300">{c.video_trends_desc || 'Preview popular avatar video templates before creating your own.'}</p>
                        </div>
                        <HomeVideoTrends />
                    </div>
                </section>
            )}

            {/* Video Creation Section */}
            {c.show_videos !== false && (
                <section id="videos" data-animate className="py-24 bg-gradient-to-b from-slate-100 via-white to-slate-50 dark:from-[#1a1612] dark:to-background-dark transition-colors duration-300">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-lg">movie</span>
                                {c.video_studio_badge}
                            </div>
                            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300"
                                dangerouslySetInnerHTML={{ __html: c.video_studio_title }} />
                            <p className="text-slate-600 dark:text-white/50 text-lg leading-relaxed transition-colors duration-300">{c.video_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.video_cards.map((item, i) => (
                                <VideoCard key={i} item={item} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Chat Section */}
            {c.show_chat !== false && (
                <section id="chat" data-animate className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-100 dark:from-background-dark dark:to-[#1a1612] transition-colors duration-300">
                    <div className="max-w-[1200px] mx-auto px-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div className="space-y-8 order-2 lg:order-1">
                                <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-lg">chat</span>
                                    Chat Studio
                                </div>
                                <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300">
                                    AI <span className="text-primary">Conversations</span> That Feel Human
                                </h2>
                                <p className="text-slate-600 dark:text-white/60 text-xl leading-relaxed transition-colors duration-300">
                                    Build custom AI personas with unique personalities, knowledge bases, and conversation styles. Perfect for customer service, creative brainstorming, or just having fun.
                                </p>
                                <ul className="space-y-4">
                                    {[
                                        'Multi-language support with 50+ languages',
                                        'Custom personality & tone configuration',
                                        'Long-term memory across conversations',
                                        'Voice input & output capabilities'
                                    ].map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-white/80 transition-colors duration-300">
                                            <span className="material-symbols-outlined text-primary text-2xl">check_circle</span>
                                            <span className="text-lg font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="relative order-1 lg:order-2">
                                <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-3xl"></div>
                                <div className="relative bg-gradient-to-br from-white to-slate-100 dark:from-[#28211b] dark:to-[#1a1410] p-8 rounded-3xl border-2 border-slate-200 dark:border-white/10 shadow-2xl transition-colors duration-300">
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="size-10 rounded-full bg-primary flex items-center justify-center text-black font-bold">AI</div>
                                            <div className="flex-1 bg-slate-100 dark:bg-white/5 p-4 rounded-2xl rounded-tl-none border border-slate-200 dark:border-white/10 transition-colors duration-300">
                                                <p className="text-slate-800 dark:text-white/90 transition-colors duration-300">Hello! I'm your AI creative assistant. How can I help you bring your ideas to life today?</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3 justify-end">
                                            <div className="flex-1 bg-primary/20 p-4 rounded-2xl rounded-tr-none border border-primary/30 max-w-[80%]">
                                                <p className="text-slate-900 dark:text-white/90 transition-colors duration-300">I need help designing a futuristic logo for my tech startup.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="size-10 rounded-full bg-primary flex items-center justify-center text-black font-bold">AI</div>
                                            <div className="flex-1 bg-slate-100 dark:bg-white/5 p-4 rounded-2xl rounded-tl-none border border-slate-200 dark:border-white/10 transition-colors duration-300">
                                                <p className="text-slate-800 dark:text-white/90 transition-colors duration-300">Great! Let's create something amazing. What's your startup's name and core values?</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Voice Section */}
            {c.show_voice !== false && (
                <section id="voice" data-animate className="py-24 bg-gradient-to-b from-slate-100 via-white to-slate-200 dark:from-[#1a1612] dark:to-background-dark transition-colors duration-300">
                    <div className="max-w-[1200px] mx-auto px-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div className="relative">
                                <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-3xl"></div>
                                <div className="relative bg-gradient-to-br from-white to-slate-100 dark:from-[#28211b] dark:to-[#1a1410] p-10 rounded-3xl border-2 border-slate-200 dark:border-white/10 shadow-2xl transition-colors duration-300">
                                    <div className="text-center space-y-6">
                                        <div className="flex justify-center gap-2">
                                            {[35, 55, 70, 45, 80, 60, 40, 72, 50, 65, 38, 58].map((height, i) => (
                                                <div
                                                    key={i}
                                                    className="w-2 bg-primary rounded-full animate-pulse"
                                                    style={{
                                                        height: `${height}px`,
                                                        animationDelay: `${i * 0.1}s`
                                                    }}
                                                ></div>
                                            ))}
                                        </div>
                                        <div className="text-slate-600 dark:text-white/60 text-sm transition-colors duration-300">Neural voice synthesis in action...</div>
                                        <div className="size-20 mx-auto bg-primary rounded-full flex items-center justify-center text-black">
                                            <span className="material-symbols-outlined text-4xl">mic</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-8">
                                <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-lg">graphic_eq</span>
                                    {c.voice_badge}
                                </div>
                                <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white transition-colors duration-300"
                                    dangerouslySetInnerHTML={{ __html: c.voice_title }} />
                                <p className="text-slate-600 dark:text-white/60 text-xl leading-relaxed transition-colors duration-300">{c.voice_desc}</p>
                                <ul className="space-y-4">
                                    {(c.voice_features || []).map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-white/80 transition-colors duration-300">
                                            <span className="material-symbols-outlined text-primary text-2xl">check_circle</span>
                                            <span className="text-lg font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section data-animate className="py-32 bg-gradient-to-b from-slate-200 via-slate-100 to-white dark:from-background-dark dark:via-[#1a1612] dark:to-background-dark relative overflow-hidden transition-colors duration-300">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent"></div>
                <div className="max-w-[1000px] mx-auto px-6 text-center relative z-10">
                    <h2 className="text-6xl md:text-7xl font-black tracking-tight mb-8 text-slate-900 dark:text-white transition-colors duration-300">
                        {c.cta_title} <span className="text-primary">{c.cta_subtitle}</span>?
                    </h2>
                    <p className="text-slate-600 dark:text-white/60 text-2xl mb-12 max-w-2xl mx-auto transition-colors duration-300">{c.cta_desc}</p>
                    <div className="flex flex-wrap justify-center gap-6">
                        <button onClick={openRegister} className="bg-primary hover:bg-primary/90 text-black px-16 py-6 rounded-xl font-black text-xl transition-all shadow-2xl shadow-primary/40 hover:shadow-primary/60 hover:scale-105">
                            {c.cta_btn_primary}
                        </button>
                        <Link href={c.hero_btn_2_url} className="bg-primary/20 hover:bg-primary/30 text-slate-900 dark:text-white px-16 py-6 rounded-xl font-bold text-xl transition-all border-2 border-primary hover:border-primary">
                            {c.cta_btn_secondary}
                        </Link>
                    </div>
                    <p className="text-white/40 text-sm mt-8">{c.cta_tagline}</p>
                </div>
            </section>
        </>
    );
}

const THEMES = {
    'dark-studio': HomeV1,
    'glassmorphism': HomeV2,
    'editorial': HomeV3,
    'cyberpunk': HomeV4,
    'aurora': HomeV5,
};

export default function Index({ auth, laravelVersion, phpVersion, popup }) {
    return (
        <>
            <HomePopup data={popup} />
            <MainLayout auth={auth}>
                <VariantRouter />
            </MainLayout>
        </>
    );
}

function VariantRouter() {
    const { openRegister } = useModals() || {};
    const { page, settings, theme } = usePage().props;

    // Support live preview via URL param: ?preview_theme=cyberpunk
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const previewTheme = params.get('preview_theme');
    const activeTheme = previewTheme || theme || 'original';

    const ThemeComponent = THEMES[activeTheme];

    if (ThemeComponent) {
        return (
            <>
                <Head>
                    <title>{settings?.title || settings?.name || 'Home'}</title>
                    <meta name="description" content={settings?.description || 'The Creative Engine'} />
                    {settings?.favicon && <link rel="icon" type="image/x-icon" href={settings.favicon} />}
                </Head>
                {previewTheme && (
                    <div className="fixed bottom-4 right-4 z-50 bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2 shadow-2xl border border-white/10">
                        <span className="material-symbols-outlined text-primary text-[14px]">visibility</span>
                        Preview: {previewTheme}
                    </div>
                )}
                <ThemeComponent page={page} openRegister={openRegister} />
            </>
        );
    }

    // Default: original IndexContent
    return <IndexContent page={page} />;
}
